import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';

import { ExternalColumnType, GetMicrostoreColumnQuery, useGetMicrostoreColumnPricesQuery } from 'data/__generated__';

import { SectionContainer } from 'components/DetailsView/Section';
import { colors } from 'constants/colors';
import { DetailFormValue } from 'components/DetailsView/DetailFormValue';
import { formatPrice } from 'utils/price';
import { ImportNayaxMDBTemplate } from './ImportNayaxMDBTemplate';
import { PrintMicrostoreColumn } from '@/pages/MicrostoreColumns/MicrostoreColumnDetails/PrintMicrostoreColumn';

type MicrostoreLocation = NonNullable<
    NonNullable<GetMicrostoreColumnQuery['microstoreColumn']>['shelves'][number]['locationTable'][number][number]
>;

export const NayaxMDBSection = ({
    microstoreColumn,
}: {
    microstoreColumn: GetMicrostoreColumnQuery['microstoreColumn'];
}) => {
    const { register, setValue, watch } = useFormContext();

    const { data: pricesData } = useGetMicrostoreColumnPricesQuery({
        variables: { microstoreColumnId: microstoreColumn._id },
    });

    const priceTTCByProductId = useMemo(() => {
        const map = new Map<string, number>();
        for (const productPrice of pricesData?.microstoreColumnPrices ?? []) {
            map.set(productPrice.productId, productPrice.priceTTC);
        }
        return map;
    }, [pricesData]);

    const handleTemplateImport = (template: Record<string, number>) => {
        microstoreColumn.shelves.forEach((shelf) => {
            shelf.locationTable.forEach((locationRow) => {
                locationRow.forEach((location) => {
                    const fieldName = `shelves.${shelf.positionInColumn}.locationTable.${location.positionInShelf.row}.${location.positionInShelf.column}.nayaxMDBCode`;
                    const key = `${shelf.positionInColumn}-${location.positionInShelf.row}-${location.positionInShelf.column}`;

                    if (template[key] !== undefined) {
                        setValue(fieldName, template[key], { shouldValidate: true });
                    }
                });
            });
        });
    };

    return (
        <SectionContainer title="Codes Nayax MDB" isInitiallyOpen>
            <Container>
                <Header>
                    {watch('externalColumnType') === ExternalColumnType.NayaxNeocorner && (
                        <PrintMicrostoreColumn microstoreColumn={microstoreColumn} />
                    )}
                    <ImportNayaxMDBTemplate onImport={handleTemplateImport} />
                </Header>
                <Table>
                    {microstoreColumn.shelves.map((shelf, shelfIndex) => (
                        <React.Fragment key={shelf._id}>
                            {shelf.locationTable.map((locationRow, locationRowIndex) => (
                                <Tr key={`${shelf._id}-row-${locationRowIndex}`}>
                                    {locationRow.map((location: MicrostoreLocation, locationColumnIndex) => {
                                        const fieldName = `shelves.${shelfIndex}.locationTable.${locationRowIndex}.${locationColumnIndex}.nayaxMDBCode`;
                                        const productArrangementName =
                                            location.microstoreLocationTemplate?.productArrangement?.name ||
                                            location.microstoreLocationTemplate?.product?.fullname ||
                                            '-';
                                        const isDoubleCoil =
                                            location.microstoreLocationTemplate?.product?.isDoubleCoil || false;
                                        const stockMax = location.microstoreLocationTemplate?.stockMax;
                                        const productId = location.microstoreLocationTemplate?.product?._id;
                                        const priceTTC = productId ? priceTTCByProductId.get(productId) : undefined;

                                        return (
                                            <Td
                                                key={`${shelf._id}-${locationColumnIndex}-mdb`}
                                                $isDoubleCoil={isDoubleCoil}
                                            >
                                                <ButtonLabel>
                                                    {isDoubleCoil
                                                        ? `[DOUBLE SPIRE] - ${productArrangementName}`
                                                        : productArrangementName}
                                                </ButtonLabel>
                                                <InfoLabel>
                                                    <span>Stock max: {stockMax ?? '-'}</span>
                                                    <span>
                                                        Prix TTC: {priceTTC !== undefined ? formatPrice(priceTTC) : '-'}
                                                    </span>
                                                </InfoLabel>
                                                <ButtonContainer>
                                                    <DetailFormValue
                                                        {...register(fieldName, {
                                                            required: false,
                                                            valueAsNumber: true,
                                                        })}
                                                        placeholder="Code MDB"
                                                        defaultValue={location.nayaxMDBCode}
                                                        type="number"
                                                        step="1"
                                                        $centerText
                                                        min="0"
                                                        style={{ width: '110px' }}
                                                    />
                                                </ButtonContainer>
                                            </Td>
                                        );
                                    })}
                                </Tr>
                            ))}
                        </React.Fragment>
                    ))}
                </Table>
            </Container>
        </SectionContainer>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow-x: auto;
`;

const Header = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 16px;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 4px;
`;

const ButtonLabel = styled.div`
    font-size: 12px;
    color: ${({ theme }) => theme.textColor};
    padding-bottom: 2px;
    text-align: center;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const InfoLabel = styled.div`
    font-size: 11px;
    color: ${({ theme }) => theme.textColor};
    display: flex;
    gap: 8px;
    justify-content: center;
    padding-bottom: 2px;
`;

const Table = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const Tr = styled.div`
    display: flex;
    width: 100%;
`;

const Td = styled.div<{ $isDoubleCoil?: boolean }>`
    padding: 2px;
    border: 1px solid ${colors.lightGrey};
    color: ${({ theme }) => theme.textColor};
    flex: ${({ $isDoubleCoil }) => ($isDoubleCoil ? '2 1 calc(200px + 6px)' : '1 1 100px')};
    display: flex;
    flex-direction: column;
    align-items: center;
`;
